/**
 * Client-side ATS (Applicant Tracking System) keyword-match scoring.
 *
 * Simple, directional scorer. Not a substitute for a real ATS parser, but
 * gives users a visible delta between their pasted resume and the tailored
 * version.
 *
 * Approach (revised May 2026 after the score-suppression audit):
 *  1. Tokenize the JD into 1-grams (no bigrams - they generated mostly
 *     cross-sentence noise, e.g. "delivery. deep" or "kubernetes partner",
 *     and dragged the denominator with sub-10% hit rates).
 *  2. Strip leading/trailing punctuation per token so "engineers." doesn't
 *     end up as a different keyword from "engineers" (the audit showed
 *     period contamination cost ~5pp on every JD with sentence-ending words
 *     that were also in the keyword list).
 *  3. Apply a tiny suffix stemmer + alias map so "engineer" / "engineering"
 *     / "engineers" collapse to one match, and "k8s" / "js" / "py" hit
 *     their canonical forms. Tech-glyph tokens (c++, c#, .net, node.js)
 *     are preserved as-is - they shouldn't be stemmed.
 *  4. Match by stem-set membership rather than substring regex. Whole-word
 *     semantics fall out of tokenization for free.
 *
 * Returns { matchedCount, missing, percentMatch }.
 */

import type { ResumeJSON } from '../types/resume';

// Curated English stop words + resume/JD filler. Extended in May 2026 to
// kill common JD verbs/qualifiers that slipped through ("drive", "develop",
// "build", etc.) and dominated the missing list with no real signal.
const STOP_WORDS = new Set([
  // Core English
  'a', 'an', 'the', 'and', 'or', 'but', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might',
  'must', 'shall', 'can', 'cannot', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 'them', 'us',
  'our', 'their', 'his', 'her', 'its', 'my', 'your', 'this', 'that', 'these', 'those',
  'to', 'of', 'in', 'on', 'at', 'by', 'for', 'with', 'about', 'against', 'between', 'into',
  'through', 'during', 'before', 'after', 'above', 'below', 'from', 'up', 'down', 'out',
  'off', 'over', 'under', 'again', 'further', 'then', 'once',
  'here', 'there', 'when', 'where', 'why', 'how', 'all', 'any', 'both', 'each', 'few', 'more',
  'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own', 'same', 'so', 'than',
  'too', 'very', 's', 't', 'd', 'll', 're', 've', 'm',
  // Resume/JD filler
  'experience', 'work', 'job', 'role', 'position', 'candidate', 'company', 'team',
  'ability', 'strong', 'excellent', 'good', 'great', 'new', 'must', 'required', 'preferred',
  'plus', 'bonus', 'etc', 'eg', 'ie', 'including', 'include', 'includes', 'included',
  'year', 'years', 'month', 'months', 'day', 'days', 'hour', 'hours',
  'well', 'ideal', 'desirable', 'responsible', 'looking', 'seeking', 'able',
  'will', 'need', 'needs', 'want', 'help', 'make', 'get', 'use', 'using', 'used',
  'also', 'across', 'within', 'per', 'via', 'while', 'whether',
  'benefit', 'benefits', 'salary', 'compensation', 'remote', 'office',
  'apply', 'please',
  // JD prose verbs/qualifiers added May 2026 (audit noise)
  'drive', 'develop', 'build', 'make', 'track', 'manage',
  'next', 'deep', 'major', 'minor', 'least', 'lots',
  'progressive', 'specialty', 'similar', 'demonstrated', 'measurable', 'working',
  'various', 'current', 'currently', 'previously',
  'partner', 'partners', 'partnering',
  'lead', 'leads', 'leader', 'leadership',
  'looking', 'seeking', 'hiring', 'hire',
]);

// Single-token aliases. Each entry collapses to its canonical form; both
// the alias and the canonical end up matching the same stem. Multi-word
// expansions (e.g. "good manufacturing practices") aren't here because we
// no longer emit bigrams - they'd never become a candidate token. If non-
// tech acronym-vs-expansion cases prove painful in real prod data, add a
// pre-tokenization phrase normalizer here.
const ALIASES = new Map<string, string>([
  ['k8s', 'kubernetes'],
  ['js', 'javascript'],
  ['ts', 'typescript'],
  ['py', 'python'],
  ['gh', 'github'],
  ['db', 'database'],
  ['dbs', 'database'],
  ['ml', 'machinelearning'],
  ['ai', 'artificialintelligence'],
  ['ux', 'userexperience'],
  ['ui', 'userinterface'],
  ['qa', 'qualityassurance'],
]);

// Light suffix stemmer. Tech-glyph tokens (c++, .net, node.js) skip
// stemming entirely. Iterates one suffix per pass until stable so that
// "migrations" -> "migration" -> "migra" lands on the same stem as
// "migration" without callers needing to invoke twice.
function stemOnce(token: string): string {
  if (token.endsWith('ization')) return token.slice(0, -7);
  if (token.endsWith('tion')) return token.slice(0, -4);
  if (token.endsWith('ment')) return token.slice(0, -4);
  if (token.endsWith('ness')) return token.slice(0, -4);
  if (token.endsWith('ies')) return token.slice(0, -3) + 'y';
  if (token.endsWith('ing') && token.length > 5) return token.slice(0, -3);
  if (token.endsWith('ed') && token.length > 4) return token.slice(0, -2);
  if (token.endsWith('es') && token.length > 4) return token.slice(0, -2);
  if (token.endsWith('s') && token.length > 3 && !token.endsWith('ss')) return token.slice(0, -1);
  return token;
}
function stem(token: string): string {
  if (/[+#.\-]/.test(token)) return token;
  if (token.length <= 3) return token;
  for (let i = 0; i < 4; i++) {
    if (token.length <= 3) break;
    const next = stemOnce(token);
    if (next === token) break;
    token = next;
  }
  return token;
}

// Apply alias before stemming so "k8s" -> "kubernetes" -> "kubernete" or
// whatever the stemmer does (irrelevant, both sides go through the same
// pipeline). Public for testing.
function normalize(token: string): string {
  return stem(ALIASES.get(token) ?? token);
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    // Keep + # . - so tokens like c++, c#, .net, node.js, safety-first survive.
    .replace(/[^a-z0-9+#.\-\s]/g, ' ')
    .split(/\s+/)
    // Strip TRAILING punctuation per token. Without this, sentence-ending
    // words like "engineers." become a different token from "engineers" -
    // the May 2026 audit showed this cost ~5pp per JD. Don't strip
    // LEADING dots: ".net" is a real tech token whose initial dot is
    // semantic, not contamination.
    .map(t => t.replace(/[.\-]+$/, ''))
    .filter(Boolean);
}

function isKeywordCandidate(token: string): boolean {
  if (token.length < 2) return false;
  if (STOP_WORDS.has(token)) return false;
  if (/^\d+$/.test(token)) return false;
  return true;
}

/**
 * Extract candidate keywords from a block of text (typically a job
 * description). Returns unique, lower-cased 1-grams filtered through the
 * stop list. Bigrams were dropped May 2026 (see file header).
 */
export function extractKeywords(text: string): string[] {
  const tokens = tokenize(text);
  const set = new Set<string>();
  for (const tok of tokens) {
    if (isKeywordCandidate(tok)) set.add(tok);
  }
  return Array.from(set);
}

export interface ATSScore {
  matchedCount: number;
  totalCount: number;
  percentMatch: number;
  matched: string[];
  missing: string[];
}

/**
 * Score how well a resume matches a job description.
 * Higher percent = more job-description keywords appear in the resume.
 */
export function scoreATSMatch(jobDescription: string, resumeText: string): ATSScore {
  const keywords = extractKeywords(jobDescription);
  const resumeStems = new Set(tokenize(resumeText).map(normalize));
  const matched: string[] = [];
  const missing: string[] = [];

  for (const kw of keywords) {
    if (resumeStems.has(normalize(kw))) matched.push(kw);
    else missing.push(kw);
  }

  const totalCount = keywords.length;
  const percentMatch = totalCount ? Math.round((matched.length / totalCount) * 100) : 0;

  return {
    matchedCount: matched.length,
    totalCount,
    percentMatch,
    matched,
    missing,
  };
}

/**
 * Job-description keywords that ALREADY appear in the candidate's source resume.
 *
 * These are the words a tailor must not lose. They are, by definition, terms
 * the candidate genuinely has and that the posting asks for, so keeping them is
 * the opposite of keyword-stuffing. Measured over a real corpus, the tailor was
 * dropping ~1.7 of these per resume while rewriting - "manager", "forecasting",
 * "regional", "finance" - which is how a rewrite can leave a resume matching
 * the job LESS than the version the candidate started with.
 *
 * Domain-neutral on purpose: it returns whatever the posting and resume share,
 * whether that is "Kubernetes" or "logistics" or "payroll". Nothing here favors
 * a technical resume.
 */
export function jdKeywordsInSource(jobDescription: string, resumeText: string): string[] {
  return scoreATSMatch(jobDescription, resumeText).matched;
}

/** Flatten a structured resume into a single text blob for keyword matching. */
export function flattenResumeForMatching(resume: ResumeJSON | null, fallbackText?: string): string {
  if (!resume) return fallbackText || '';
  const parts: string[] = [];
  if (resume.name) parts.push(resume.name);
  if (resume.title) parts.push(resume.title);
  if (resume.summary) parts.push(resume.summary);
  if (Array.isArray(resume.skills)) parts.push(resume.skills.join(' '));
  if (Array.isArray(resume.experience)) {
    for (const exp of resume.experience) {
      if (exp.title) parts.push(exp.title);
      if (exp.company) parts.push(exp.company);
      if (Array.isArray(exp.bullets)) parts.push(exp.bullets.join(' '));
    }
  }
  if (Array.isArray(resume.education)) {
    for (const edu of resume.education) {
      if (edu.degree) parts.push(edu.degree);
      if (edu.school) parts.push(edu.school);
    }
  }
  return parts.join(' ');
}
